import { AsyncPipe } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, effect, forwardRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule } from '@jsverse/transloco';
import { catchAndLogError } from '@rma/generic/rxjs/operators/catch-and-log-error';
import { BaseControlValueAccessor } from '@rma/generic/ui/controls/base-control';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { KeyOrTranslationObject } from '@rma/generic/util/translation-object';
import { debounceTime, distinctUntilChanged, filter, map, Observable, of, switchMap } from 'rxjs';
import type { Address, LocationAutoCompleteResult } from './address.types';
import { AddressAutocompleteComponent } from './address-autocomplete/address-autocomplete.component';
import { LOCALITY_LOOKUP } from './locality-lookup.token';

@Component({
  selector: 'rma-address-control',
  templateUrl: './address-control.component.html',
  imports: [
    AddressAutocompleteComponent,
    AsyncPipe,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    RmaIconComponent,
    TranslocoModule,
  ],
  host: {
    class: 'block',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => AddressControlComponent),
    },
  ],
})
export class AddressControlComponent extends BaseControlValueAccessor<Address> {
  private readonly localityLookup = inject(LOCALITY_LOOKUP);

  protected readonly showManualInput = signal(false);
  protected readonly disabled = signal(false);

  public readonly required = input(false, { transform: booleanAttribute });
  public readonly autoFocus = input(true, { transform: booleanAttribute });
  public readonly label = input<KeyOrTranslationObject>({
    key: 'form.label.property-address',
  });
  public readonly prefix = input(true, { transform: booleanAttribute });
  // Hides the "manually enter address" escape hatch. Defaults to false so the button shows
  // everywhere; callers that only want the autocomplete (e.g. a guided funnel) can opt out.
  public readonly hideManualEntry = input(false, { transform: booleanAttribute });
  // Hides the example-format hint under the field. Defaults to false so it shows everywhere.
  public readonly hideHint = input(false, { transform: booleanAttribute });
  // Optional custom placeholder for the search input; empty leaves it to the floating label.
  public readonly placeholder = input('');
  public readonly error = input<string | null>(null);

  public readonly isManualInputForm = output<boolean>();

  public readonly addressForm = this.createForm();

  public readonly options$: Observable<LocationAutoCompleteResult[]> =
    this.addressForm.controls.manualInput.controls.locality.valueChanges.pipe(
      filter(() => this.showManualInput()),
      debounceTime(200),
      distinctUntilChanged(),
      filter((value): value is string => typeof value === 'string' && !!value),
      switchMap((value) => this.getResults(value)),
      takeUntilDestroyed(),
    );

  // The suburb pick is what supplies postcode, state and id, so later manual edits re-save against it.
  private selectedLocality: LocationAutoCompleteResult | null = null;

  constructor() {
    super();

    effect(() => {
      this.setFormValidation(this.required(), this.showManualInput());
    });

    this.addressForm.controls.searchInput.valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(() => !this.showManualInput()),
      )
      .subscribe((x) => this.updateValue(x));

    this.addressForm.controls.manualInput.valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(() => this.showManualInput()),
      )
      .subscribe(() => this.onManualInputChanged());
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public override writeValue(value: Address | null): void {
    // The autocomplete seeds its input from the bound value once, so a pre-render write must reach it.
    // Not routed through updateValue: a parent write must never echo back through onChange.
    this.value = value;
    this.cdr.markForCheck();
    this.addressForm.controls.searchInput.patchValue(value, { emitEvent: false });
  }

  public onShowManualInput(required: boolean, showManualInput: boolean) {
    this.showManualInput.set(showManualInput);
    this.selectedLocality = null;
    this.setFormValidation(required, showManualInput);
    this.isManualInputForm.emit(showManualInput);
  }

  public mapManualInput(location: LocationAutoCompleteResult): Address | null {
    if (!this.addressForm.controls.manualInput.valid) {
      return null;
    }

    return {
      ...this.addressForm.controls.manualInput.getRawValue(),
      locality: location.rawName,
      localityId: +location.id,
      postcode: location.postcode,
      state: location.state,
    };
  }

  public displayFn(result: LocationAutoCompleteResult): string {
    return result && result.name ? result.name : '';
  }

  private onManualInputChanged(): void {
    const locality = this.addressForm.controls.manualInput.controls.locality.value;

    if (locality && typeof locality === 'object') {
      // SAFETY: the autocomplete writes the picked option object into this string-typed control.
      this.selectedLocality = locality as LocationAutoCompleteResult;
    } else if (this.selectedLocality) {
      // Typing over the picked suburb invalidates the postcode and state it supplied.
      this.selectedLocality = null;
      this.updateValue(null);
      return;
    }

    if (this.selectedLocality) {
      this.updateValue(this.mapManualInput(this.selectedLocality));
    }
  }

  private getResults(searchTerm: string): Observable<LocationAutoCompleteResult[]> {
    return this.localityLookup.getLocations({ searchTerm, subType: 'Locality' }).pipe(
      map(({ results }) =>
        results.map(({ id, name, rawName, postcode, stateAbbreviation: state }) => ({
          id,
          name,
          rawName,
          postcode,
          state,
        })),
      ),
      catchAndLogError<LocationAutoCompleteResult[]>(of([])),
    );
  }

  private createForm() {
    return new FormGroup({
      searchInput: new FormControl<Address | null>(null),
      manualInput: new FormGroup({
        unitNumber: new FormControl<string | undefined>(undefined, {
          nonNullable: true,
        }),
        streetNumber: new FormControl<string | undefined>(undefined, {
          nonNullable: true,
        }),
        street: new FormControl<string | undefined>(undefined, {
          nonNullable: true,
        }),
        locality: new FormControl<string | undefined>(undefined, {
          nonNullable: true,
        }),
      }),
    });
  }

  private setFormValidation(required: boolean, manualInput: boolean) {
    const validators = required ? [Validators.required] : [];

    this.addressForm.controls.searchInput.clearValidators();
    this.addressForm.controls.manualInput.controls.streetNumber.clearValidators();
    this.addressForm.controls.manualInput.controls.street.clearValidators();
    this.addressForm.controls.manualInput.controls.locality.clearValidators();

    if (manualInput) {
      this.addressForm.controls.manualInput.controls.streetNumber.setValidators(validators);
      this.addressForm.controls.manualInput.controls.street.setValidators(validators);
      this.addressForm.controls.manualInput.controls.locality.setValidators(validators);
    } else {
      this.addressForm.controls.manualInput.reset();

      this.addressForm.controls.searchInput.setValidators(validators);
    }

    this.addressForm.updateValueAndValidity();
  }
}
